import React, { useState } from "react";

import {
  CHEQUE_NUMBER_LENGTH,
  INDIAN_CURRENCY_ID,
  KH_VERTICAL_ID,
  LEARN_PAYMENT_GATEWAY_PROVIDERS,
  LEARN_PAYMENT_MODE_OPTIONS,
  LEARN_VERTICAL_ID,
  MAX_BANK_TRANSFER_REFERENCE_ID_LENGTH,
  PAYMENT_MODE,
  PLUTUS_PAYMENT_LINK_SOURCE,
  RAZORPAY_PAYMENT_GETWAY_PROVIDER,
  SCA_PAYMENT_GATEWAY_PROVIDERS,
  SCA_PAYMENT_MODE_OPTIONS,
} from "constants/config";
import {
  DatePicker,
  PrimaryButton,
  SingleSelect,
  Text,
  Textfield,
  ErrorMessage,
} from "widgets";
import Loader from "Shared/Loader/Loader";
import dayjs from "dayjs";
import { useSnackbarContext } from "widgets/SnackBar/SnackBar";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { toast } from "react-fox-toast";
import {
  useBankList,
  useCreatePaymentLink,
  usePunchPayment,
} from "store/backend/assets";

import styles from "./PaymentModes.module.scss";

const getPaymentModeOptions = (selectedOrderVerticalId) => {
  return selectedOrderVerticalId === LEARN_VERTICAL_ID
    ? LEARN_PAYMENT_MODE_OPTIONS
    : SCA_PAYMENT_MODE_OPTIONS;
};

const buildPayloadForCashPayment = (orderId, amount, createdBy) => ({
  order_id: orderId,
  amount: amount,
  currency_id: INDIAN_CURRENCY_ID,
  cash: {
    created_by: createdBy,
  },
});

const buildPayloadForChequePayment = (
  orderId,
  amount,
  chequeDetails,
  createdBy
) => ({
  order_id: orderId,
  amount: amount,
  currency_id: INDIAN_CURRENCY_ID,
  cheque: {
    cheque_number: chequeDetails.chequeNumber,
    bank_id: chequeDetails.bankId,
    created_by: createdBy,
    pdc_date: chequeDetails.pdcDate,
  },
});

const buildPayloadForBankTransferPayment = (
  orderId,
  amount,
  bankTransferDetails,
  createdBy,
  referenceId
) => ({
  order_id: orderId,
  amount: amount,
  currency_id: INDIAN_CURRENCY_ID,
  bank_transfer: {
    reference_transaction_id: referenceId,
    bank_id: bankTransferDetails.bankId,
    created_by: createdBy,
  },
});
const handleOnClickPunchPaymentButton = (
  orderId,
  amountToBePaid,
  createdBy,
  paymentMode,
  chequeDetails,
  bankTransferDetails,
  referenceId,
  punchPayment,
  openSnackbar,
  setIsLinkCopied,
  setPaymentLink,
  createPaymentLink,
  paymentTypeId,
  paymentFlowTypeId,
  paymentGatewayProvider
) => {
  const payload =
    paymentMode === PAYMENT_MODE.CASH
      ? buildPayloadForCashPayment(orderId, amountToBePaid, createdBy)
      : paymentMode === PAYMENT_MODE.CHEQUE
      ? buildPayloadForChequePayment(
          orderId,
          amountToBePaid,
          chequeDetails,
          createdBy
        )
      : paymentMode === PAYMENT_MODE.BANK_TRANSFER
      ? buildPayloadForBankTransferPayment(
          orderId,
          amountToBePaid,
          bankTransferDetails,
          createdBy,
          referenceId
        )
      : null;

  if (paymentMode === PAYMENT_MODE.ONLINE) {
    handleCreatePaymentLink(
      orderId,
      amountToBePaid,
      setPaymentLink,
      setIsLinkCopied,
      createPaymentLink,
      paymentTypeId,
      paymentFlowTypeId,
      paymentGatewayProvider
    );
    return;
  }

  if (!payload) {
    openSnackbar("Invalid payment mode selected", "error");
    return;
  }

  punchPayment(payload, {
    onSuccess: () => {
      openSnackbar("Payment successful", "success");
      setTimeout(() => window.location.reload(), 1000);
    },
    onError: (error) => {
      openSnackbar(error.message, "error");
    },
  });
};
const shouldEnablePunchPaymentButton = (
  paymentMode,
  chequeNumber,
  bankId,
  pdcDate,
  bankReferenceId,
  amountToBePaid,
  firstPaymentCollectionAmount
) => {
  if (
    firstPaymentCollectionAmount &&
    amountToBePaid < firstPaymentCollectionAmount
  ) {
    return true;
  }

  if (!paymentMode) {
    return true;
  }
  if (amountToBePaid <= 0) {
    return true;
  }

  if (paymentMode === PAYMENT_MODE.ONLINE) {
    return false;
  }

  if (paymentMode === PAYMENT_MODE.CASH) {
    return false;
  }

  if (paymentMode === PAYMENT_MODE.CHEQUE) {
    return (
      !chequeNumber ||
      chequeNumber.length !== CHEQUE_NUMBER_LENGTH ||
      !bankId ||
      !pdcDate
    );
  }

  if (paymentMode === PAYMENT_MODE.BANK_TRANSFER) {
    return !bankReferenceId;
  }

  return true;
};
const handleCreatePaymentLink = (
  orderId,
  amountToBePaid,
  setPaymentLink,
  setIsLinkCopied,
  createPaymentLink,
  paymentTypeId,
  paymentFlowTypeId,
  paymentGatewayProvider
) => {
  const payload = {
    order_id: orderId,
    payment_link_amount: amountToBePaid,
    currency_id: INDIAN_CURRENCY_ID,
    payment_type_id: paymentTypeId,
    payment_link_source_id: PLUTUS_PAYMENT_LINK_SOURCE,
    payment_flow_type_id: paymentFlowTypeId,
    payment_provider_id: paymentGatewayProvider,
  };

  createPaymentLink(payload, {
    onSuccess: (paymentLink) => {
      setIsLinkCopied(false);
      setPaymentLink(paymentLink);
    },
  });
};
const handleCopyLink = (paymentLink, setIsLinkCopied, setPaymentLink) => {
  navigator.clipboard.writeText(paymentLink);
  setIsLinkCopied(true);
  setPaymentLink("");
  toast.success("Copied Payment Link!");
};
const handleChangePaymentMode = (event, setPaymentLink, setPaymentMode) => {
  setPaymentMode(event.target.value);
  setPaymentLink("");
};
const PaymentModes = (props) => {
  const {
    orderId,
    amountToBePaid,
    createdBy,
    paymentLink,
    setPaymentLink,
    paymentTypeId,
    paymentFlowTypeId,
    firstPaymentCollectionAmount,
    selectedOrderVerticalId,
  } = props;
  const { openSnackbar } = useSnackbarContext();
  const [chequeNumber, setChequeNumber] = useState("");
  const [bankId, setBankId] = useState(null);
  const [pdcDate, setPdcDate] = useState("");
  const [bankReferenceId, setBankReferenceId] = useState("");
  const [isLinkCopied, setIsLinkCopied] = useState(false);
  const [paymentGatewayProvider, setPaymentGatewayProvider] = useState(
    RAZORPAY_PAYMENT_GETWAY_PROVIDER.value
  );
  const [paymentMode, setPaymentMode] = useState(PAYMENT_MODE.ONLINE);
  const { data: bankList, isPending: isBankListLoading } = useBankList();
  const { mutate: punchPayment, isPending: isPunchPaymentLoading } =
    usePunchPayment();
  const {
    mutate: createPaymentLink,
    isPending: isCreatePaymentLinkLoading,
    isError: isErrorOnCreatePaymentLink,
    error: errorOnCreatePaymentLink,
  } = useCreatePaymentLink();
  const bankMenuItems = bankList?.map(({ id, name }) => ({
    key: id,
    value: id,
    label: name,
  }));
  const minDate = dayjs().subtract(21, "day");
  const maxDate = dayjs().add(1, "year");

  return (
    <div className={styles.mainContainer}>
      <div className={styles.field}>
        <Text>Select Payment Mode</Text>
        <SingleSelect
          value={paymentMode}
          menuItems={getPaymentModeOptions(selectedOrderVerticalId)}
          handleChange={(event) =>
            handleChangePaymentMode(event, setPaymentLink, setPaymentMode)
          }
          tag="Select Payment Mode"
        />
      </div>
      {paymentMode === PAYMENT_MODE.ONLINE && (
        <div className={styles.field}>
          <Text>Select Payment Provider</Text>
          <SingleSelect
            value={
              selectedOrderVerticalId === KH_VERTICAL_ID
                ? RAZORPAY_PAYMENT_GETWAY_PROVIDER.value
                : paymentGatewayProvider
            }
            menuItems={
              selectedOrderVerticalId === KH_VERTICAL_ID
                ? SCA_PAYMENT_GATEWAY_PROVIDERS
                : LEARN_PAYMENT_GATEWAY_PROVIDERS
            }
            handleChange={(event) =>
              setPaymentGatewayProvider(event.target.value)
            }
            disabled={selectedOrderVerticalId === KH_VERTICAL_ID}
          />
        </div>
      )}
      {paymentMode === PAYMENT_MODE.CHEQUE && (
        <div className={styles.chequePaymentContainer}>
          <div className={styles.field}>
            <Text>Please enter cheque number</Text>
            <Textfield
              value={chequeNumber}
              handleOnChange={(e) => {
                const onlyNumbers = e.target.value
                  .replace(/\D/g, "")
                  .slice(0, CHEQUE_NUMBER_LENGTH);
                setChequeNumber(onlyNumbers);
              }}
              placeholder="Enter Cheque Number"
            />
          </div>
          <div className={styles.flexContainer}>
            {isBankListLoading ? (
              <Loader />
            ) : (
              <div className={styles.field}>
                <Text>Please select bank</Text>
                <SingleSelect
                  value={bankId}
                  menuItems={bankMenuItems}
                  handleChange={(e) => setBankId(e.target.value)}
                  placeholder="Select Bank"
                  tag="Bank"
                />
              </div>
            )}
            <div className={styles.field}>
              <Text>Please select cheque date</Text>
              <DatePicker
                value={pdcDate}
                onChange={(date) => setPdcDate(date)}
                placeholder="Select Cheque Date"
                minDate={minDate}
                maxDate={maxDate}
              />
            </div>
          </div>
        </div>
      )}
      {paymentMode === PAYMENT_MODE.BANK_TRANSFER && (
        <div className={styles.bankTransferContainer}>
          <div className={styles.field}>
            <Text>Please enter reference ID</Text>
            <Textfield
              value={bankReferenceId}
              handleOnChange={(e) => {
                const validInput = e.target.value
                  .replace(/\s+/g, "")
                  .slice(0, MAX_BANK_TRANSFER_REFERENCE_ID_LENGTH);
                setBankReferenceId(validInput);
              }}
              placeholder="Enter Reference ID"
            />
          </div>
          <div className={styles.flexContainer}>
            {isBankListLoading ? (
              <Loader />
            ) : (
              <div className={styles.field}>
                <Text>Please select bank</Text>
                <SingleSelect
                  value={bankId}
                  menuItems={bankMenuItems}
                  handleChange={(e) => setBankId(e.target.value)}
                  placeholder="Select Bank"
                  tag="Bank"
                />
              </div>
            )}
          </div>
        </div>
      )}

      <div className={styles.submitButton}>
        <PrimaryButton
          onClick={() =>
            handleOnClickPunchPaymentButton(
              orderId,
              amountToBePaid,
              createdBy,
              paymentMode,
              { chequeNumber, bankId, pdcDate },
              { bankId },
              bankReferenceId,
              punchPayment,
              openSnackbar,
              setIsLinkCopied,
              setPaymentLink,
              createPaymentLink,
              paymentTypeId,
              paymentFlowTypeId,
              paymentGatewayProvider
            )
          }
          disabled={shouldEnablePunchPaymentButton(
            paymentMode,
            chequeNumber,
            bankId,
            pdcDate,
            bankReferenceId,
            amountToBePaid,
            firstPaymentCollectionAmount
          )}
        >
          {isPunchPaymentLoading || isCreatePaymentLinkLoading ? (
            <Loader />
          ) : paymentMode === PAYMENT_MODE.ONLINE ? (
            "Create Payment Link"
          ) : (
            "Punch Payment"
          )}{" "}
        </PrimaryButton>
      </div>
      {paymentLink && (
        <div
          className={styles.paymentLinkContainer}
          onClick={() =>
            handleCopyLink(paymentLink, setIsLinkCopied, setPaymentLink)
          }
        >
          <div className={styles.copyLinkText}>Copy Link</div>
          <ContentCopyIcon className={styles.copyIcon} />
        </div>
      )}
      {isErrorOnCreatePaymentLink && (
        <ErrorMessage>{errorOnCreatePaymentLink.message}</ErrorMessage>
      )}
    </div>
  );
};

export default PaymentModes;
